// SUPERADMIN dashboard — full-screen overlay, opened via Events.AdminOpen
// (the CLOUD panel exposes the entry button only when the logged-in account
// is an admin). Every data call goes through accountManager.admin.*, which
// hits the isAdmin-gated server endpoints, so a non-admin can't see anything
// even if they force the panel open.
//
// Two tabs: OVERVIEW (analytics) and USERS (searchable list → detail drawer
// with grant-coin / ban / admin-toggle actions).

import { Events, on } from './eventBus.js'
import { admin } from '../game/accountManager.js'
import { formatCoins } from './format.js'

export function mountAdminPanel({ root }) {
  root.className = 'admin'
  root.innerHTML = `
    <div class="admin__card">
      <div class="admin__header">
        <h2>SUPERADMIN <span>超级管理</span></h2>
        <div class="admin__tabs">
          <button data-tab="overview" class="active">OVERVIEW</button>
          <button data-tab="users">USERS</button>
        </div>
        <button class="admin__close" data-admin-close aria-label="Close">×</button>
      </div>
      <div class="admin__body" data-admin-body></div>
    </div>
  `

  const body = root.querySelector('[data-admin-body]')
  const tabs = root.querySelectorAll('[data-tab]')
  let tab = 'overview'

  const close = () => root.classList.remove('active')
  root.querySelector('[data-admin-close]').addEventListener('click', close)
  root.addEventListener('click', (e) => { if (e.target === root) close() })

  tabs.forEach((t) => t.addEventListener('click', () => {
    tab = t.dataset.tab
    tabs.forEach((x) => x.classList.toggle('active', x === t))
    renderTab()
  }))

  async function renderTab() {
    body.innerHTML = `<div class="admin__loading">Loading…</div>`
    try {
      if (tab === 'overview') await renderOverview()
      else await renderUsers()
    } catch (err) {
      body.innerHTML = `<div class="admin__error">Failed to load — ${esc(String(err.message || err))}. (Are you logged in as an admin?)</div>`
    }
  }

  async function renderOverview() {
    const o = await admin.overview()
    const card = (label, value) =>
      `<div class="admin__stat"><div class="admin__stat-val">${value}</div><div class="admin__stat-lbl">${label}</div></div>`
    const kRows = Object.entries(o.kingdoms).map(([k, v]) =>
      `<tr><td>${k}</td><td>${v.players}</td><td>${formatCoins(v.wealth)}</td><td>${v.citizens}</td></tr>`,
    ).join('')
    const signups = (o.recentSignups ?? []).map((s) =>
      `<div class="admin__signup"><span>${esc(s.email)}</span><span class="dim">${s.kingdom ?? '—'} · ${timeAgo(s.createdAt)}</span></div>`,
    ).join('') || '<div class="dim">No registered accounts yet.</div>'
    body.innerHTML = `
      <div class="admin__stats">
        ${card('Accounts', o.totalAccounts)}
        ${card('Registered', o.registered)}
        ${card('Guests', o.guests)}
        ${card('Admins', o.admins)}
        ${card('Kingdom Club', o.members)}
        ${card('Banned', o.banned)}
        ${card('Total Wealth', formatCoins(o.totalWealth))}
        ${card('Total Citizens', o.totalCitizens)}
      </div>
      <div class="admin__section-title">BY KINGDOM</div>
      <table class="admin__table">
        <thead><tr><th>Kingdom</th><th>Players</th><th>Wealth</th><th>Citizens</th></tr></thead>
        <tbody>${kRows}</tbody>
      </table>
      <div class="admin__section-title">RECENT SIGNUPS</div>
      <div class="admin__signups">${signups}</div>
    `
  }

  async function renderUsers(query = '') {
    const { users, total } = await admin.users(query)
    const rows = users.map((u) => `
      <tr data-user="${u.userId}">
        <td>${esc(u.name ?? '—')}<div class="dim">${esc(u.email ?? 'guest')}</div></td>
        <td>${u.kingdom ?? '—'}</td>
        <td>${formatCoins(u.wealth)}</td>
        <td>${u.membership === 'KINGDOM_CLUB' ? '★ Club' : 'Free'}</td>
        <td>${statusBadge(u)}</td>
        <td class="dim">${timeAgo(u.updatedAt)}</td>
      </tr>
    `).join('')
    body.innerHTML = `
      <div class="admin__search">
        <input data-admin-search placeholder="search email / name / id…" value="${esc(query)}" />
        <span class="dim">${users.length} of ${total}</span>
      </div>
      <table class="admin__table admin__table--users">
        <thead><tr><th>Player</th><th>Kingdom</th><th>Wealth</th><th>Tier</th><th>Status</th><th>Active</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="6" class="dim">No matches.</td></tr>'}</tbody>
      </table>
      <div class="admin__drawer" data-admin-drawer></div>
    `
    const search = body.querySelector('[data-admin-search]')
    let t = null
    search.addEventListener('input', () => {
      clearTimeout(t)
      t = setTimeout(() => renderUsers(search.value.trim()), 250)
    })
    body.querySelectorAll('[data-user]').forEach((tr) =>
      tr.addEventListener('click', () => openDrawer(tr.dataset.user)),
    )
  }

  async function openDrawer(userId) {
    const drawer = body.querySelector('[data-admin-drawer]')
    if (!drawer) return
    drawer.classList.add('open')
    drawer.innerHTML = `<div class="admin__loading">Loading user…</div>`
    const u = await admin.user(userId)
    const p = u.state?.profile ?? {}
    const home = p.homeKingdom
    const k = home ? u.state?.kingdoms?.[home] : null
    const marriages = (p.marriages ?? []).filter((m) => m.status === 'ACTIVE')
    drawer.innerHTML = `
      <div class="admin__drawer-head">
        <div>
          <div class="admin__drawer-name">${esc(p.catName ?? '—')} ${u.isAdmin ? '<span class="admin__tag">ADMIN</span>' : ''} ${u.banned ? '<span class="admin__tag ban">BANNED</span>' : ''}</div>
          <div class="dim">${esc(u.email ?? 'guest')} · ${u.userId}</div>
        </div>
        <button class="admin__drawer-close" data-drawer-close>×</button>
      </div>
      <div class="admin__kv">
        ${kv('Kingdom', home ?? '—')}
        ${kv('Class / Title', `${p.classId ?? '—'} / ${p.title ?? '—'}`)}
        ${kv('Membership', p.membership ?? 'FREE')}
        ${kv('Location', p.geo?.label ?? 'not shared')}
        ${kv('Cash', formatCoins(k?.wealth ?? 0))}
        ${kv('Bounties', formatCoins(k?.bountyTotal ?? 0))}
        ${kv('Goons slain', k?.goonsSlain ?? 0)}
        ${kv('Clicks', k?.clickCount ?? 0)}
        ${kv('Buildings', (k?.buildings ?? []).length)}
        ${kv('Plots', k?.plots ? Object.keys(k.plots).length : 0)}
        ${kv('Active marriages', marriages.length)}
        ${kv('Children', (p.children ?? []).length)}
        ${kv('Joined', p.joinedAt ? new Date(p.joinedAt).toLocaleDateString() : '—')}
      </div>
      <div class="admin__actions">
        <div class="admin__grant">
          <input type="number" data-grant-amount placeholder="amount" />
          <button data-grant>GRANT COIN</button>
        </div>
        <button data-ban class="${u.banned ? 'unban' : 'ban'}">${u.banned ? 'UNBAN' : 'BAN'}</button>
        <button data-toggle-admin>${u.isAdmin ? 'REVOKE ADMIN' : 'MAKE ADMIN'}</button>
      </div>
      <div class="admin__action-msg" data-action-msg></div>
    `
    const msg = drawer.querySelector('[data-action-msg]')
    drawer.querySelector('[data-drawer-close]').addEventListener('click', () => drawer.classList.remove('open'))
    drawer.querySelector('[data-grant]').addEventListener('click', async () => {
      const amt = Number(drawer.querySelector('[data-grant-amount]').value)
      const r = await safe(() => admin.grant(userId, amt))
      msg.textContent = r.ok ? `Granted. New cash: ${formatCoins(r.val.wealth)}` : `Failed: ${r.err}`
      if (r.ok) renderUsers(body.querySelector('[data-admin-search]')?.value ?? '')
    })
    drawer.querySelector('[data-ban]').addEventListener('click', async () => {
      const r = await safe(() => admin.ban(userId, !u.banned))
      msg.textContent = r.ok ? `Account ${r.val.banned ? 'suspended' : 'reinstated'}.` : `Failed: ${r.err}`
      if (r.ok) openDrawer(userId)
    })
    drawer.querySelector('[data-toggle-admin]').addEventListener('click', async () => {
      const r = await safe(() => admin.setAdmin(userId, !u.isAdmin))
      msg.textContent = r.ok ? `Admin ${r.val.isAdmin ? 'granted' : 'revoked'}.` : `Failed: ${r.err}`
      if (r.ok) openDrawer(userId)
    })
  }

  on(Events.AdminOpen, () => {
    root.classList.add('active')
    tab = 'overview'
    tabs.forEach((x) => x.classList.toggle('active', x.dataset.tab === 'overview'))
    renderTab()
  })
}

async function safe(fn) {
  try { return { ok: true, val: await fn() } }
  catch (err) { return { ok: false, err: String(err.message || err) } }
}

function statusBadge(u) {
  if (u.banned) return '<span class="admin__badge ban">banned</span>'
  if (u.isAdmin) return '<span class="admin__badge admin">admin</span>'
  if (!u.registered) return '<span class="admin__badge guest">guest</span>'
  return '<span class="admin__badge ok">active</span>'
}

function kv(label, value) {
  return `<div class="admin__kv-row"><span class="admin__kv-label">${label}</span><span class="admin__kv-val">${value}</span></div>`
}

function timeAgo(ms) {
  if (!ms) return '—'
  const s = Math.floor((Date.now() - ms) / 1000)
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]))
}
